import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
} from 'react-native';
import {
  IconButton,
  useTheme,
  ActivityIndicator,
  Chip,
  Button,
  Dialog,
  Portal,
  Paragraph,
  Surface,
} from 'react-native-paper';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import TruckService from '../services/truckService';
import { Truck } from '../types/Truck';
import { ErrorLogger } from '../utils/errorLogger';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { ServiceSummaryByTruck } from '../types/Service';
import { useFirestoreSubscription } from '../hooks/useFirestoreSubscription';

type FilterStatus = 'all' | 'active' | 'inactive';

interface TruckListProps {
  limit?: number;
  showHeader?: boolean;
  embedded?: boolean;
  onTruckPress?: (truck: Truck) => void;
  onDeleteSuccess?: () => void;
  serviceSummary?: ServiceSummaryByTruck;
  searchQuery?: string;
  filterStatus?: FilterStatus;
}

const TruckList: React.FC<TruckListProps> = ({
  limit: _limit,
  showHeader: _showHeader = false,
  embedded: _embedded = false,
  onTruckPress,
  onDeleteSuccess,
  serviceSummary = {},
  searchQuery = '',
  filterStatus = 'all',
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data: subscribedTrucks, loading: subscriptionLoading } = useFirestoreSubscription(
    (onUpdate, onError) => {
      if (!user?.uid) { onUpdate([]); return () => {}; }
      return TruckService.subscribeToAllTrucks(onUpdate, onError);
    },
    [] as Truck[],
  );
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [truckToDelete, setTruckToDelete] = useState<Truck | null>(null);
  const [deletingTruck, setDeletingTruck] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({ visible: false, title: '', message: '' });

  const displayTrucks = useMemo(() => {
    const base = subscribedTrucks;
    return base.filter(t => {
      if (filterStatus === 'active' && !t.isActive) return false;
      if (filterStatus === 'inactive' && t.isActive) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          t.plateNumber.toLowerCase().includes(q) ||
          (t.model ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [subscribedTrucks, filterStatus, searchQuery]);

  const handleDeleteTruck = (truck: Truck) => {
    if (!truck.id) return;
    setTruckToDelete(truck);
    setDeleteDialogVisible(true);
  };

  const confirmDeleteTruck = async () => {
    if (!truckToDelete?.id) return;
    try {
      setDeletingTruck(true);
      await TruckService.deleteTruck(truckToDelete.id);
      setDeleteDialogVisible(false);
      setTruckToDelete(null);
      onDeleteSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'TruckList.confirmDeleteTruck');
      setDeleteDialogVisible(false);
      setTruckToDelete(null);
      setErrorDialog({ visible: true, title: 'Error', message: 'Failed to delete truck. Please try again.' });
    } finally {
      setDeletingTruck(false);
    }
  };

  const handleToggleStatus = async (truck: Truck) => {
    if (!truck.id) return;
    try {
      await TruckService.toggleTruckStatus(truck.id);
      onDeleteSuccess?.();
    } catch (error) {
      ErrorLogger.logError(error as any, 'TruckList.handleToggleStatus');
      Alert.alert('Error', 'Failed to update truck status. Please try again.');
    }
  };

  const getHealthStatus = (truckId: string | undefined): { label: string; color: string } => {
    if (!truckId || !serviceSummary[truckId]) return { label: 'Optimal', color: '#2e7d32' };
    const s = serviceSummary[truckId];
    if (s.overdue > 0) return { label: 'Service Req.', color: theme.colors.error };
    if (s.dueSoon > 0) return { label: 'Due Soon', color: theme.colors.tertiary };
    return { label: 'Optimal', color: '#2e7d32' };
  };

  const renderTruckItem = ({ item }: { item: Truck }) => {
    const health = getHealthStatus(item.id);

    return (
      <TouchableOpacity
        activeOpacity={0.75}
        onPress={() => onTruckPress?.(item)}
      >
        <Surface style={[styles.card, { backgroundColor: theme.colors.surface }]} elevation={1}>
          {/* Top row: icon + main info + status chip */}
          <View style={styles.cardTop}>
            {/* Vehicle icon */}
            <View style={[styles.vehicleIconBox, { backgroundColor: item.isActive ? theme.colors.primaryContainer : theme.colors.surfaceVariant }]}>
              <MaterialCommunityIcons
                name="truck"
                size={28}
                color={item.isActive ? theme.colors.primary : theme.colors.onSurfaceVariant}
              />
            </View>

            {/* Main info */}
            <View style={styles.cardMainInfo}>
              <View style={styles.cardTitleRow}>
                <Text style={[styles.plateText, { color: theme.colors.onSurface }]} numberOfLines={1}>
                  {item.plateNumber}
                </Text>
                <View style={[styles.statusChip, { backgroundColor: item.isActive ? theme.colors.secondaryContainer : theme.colors.elevation.level3 }]}>
                  <Text style={[styles.statusText, { color: item.isActive ? theme.colors.onSecondaryContainer : theme.colors.onSurfaceVariant }]}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </Text>
                </View>
              </View>

              {(item.model || item.color) && (
                <Text style={[styles.modelText, { color: theme.colors.onSurfaceVariant }]} numberOfLines={1}>
                  {[item.model, item.color].filter(Boolean).join(' · ')}
                </Text>
              )}

              {/* Service badges */}
              {item.id && serviceSummary[item.id] && (
                <View style={styles.badgeRow}>
                  {(serviceSummary[item.id].overdue ?? 0) > 0 && (
                    <Chip compact mode="flat" style={styles.overdueChip} textStyle={styles.overdueChipText}>
                      {serviceSummary[item.id].overdue} overdue
                    </Chip>
                  )}
                  {(serviceSummary[item.id].overdue ?? 0) === 0 && (serviceSummary[item.id].dueSoon ?? 0) > 0 && (
                    <Chip compact mode="flat" style={styles.dueSoonChip} textStyle={styles.dueSoonChipText}>
                      {serviceSummary[item.id].dueSoon} due soon
                    </Chip>
                  )}
                </View>
              )}
            </View>

            <MaterialCommunityIcons
              name="chevron-right"
              size={20}
              color={theme.colors.onSurfaceVariant}
              style={styles.chevron}
            />
          </View>

          {/* Metadata row */}
          <View style={[styles.metaRow, { borderTopColor: theme.colors.outlineVariant }]}>
            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="calendar-clock" size={13} color={theme.colors.onSurfaceVariant} />
              <Text style={[styles.metaLabel, { color: theme.colors.onSurfaceVariant }]}>Added</Text>
              <Text style={[styles.metaValue, { color: theme.colors.onSurface }]}>
                {item.createdAt.toLocaleDateString('en-UG', { day: 'numeric', month: 'short' })}
              </Text>
            </View>

            <View style={[styles.metaDivider, { backgroundColor: theme.colors.outlineVariant }]} />

            <View style={styles.metaItem}>
              <MaterialCommunityIcons name="heart-pulse" size={13} color={health.color} />
              <Text style={[styles.metaLabel, { color: theme.colors.onSurfaceVariant }]}>Health</Text>
              <Text style={[styles.metaValue, { color: health.color }]}>{health.label}</Text>
            </View>
          </View>

          {/* Action row */}
          <View style={styles.actionRow}>
            <Button
              mode="text"
              icon={item.isActive ? 'pause-circle-outline' : 'play-circle-outline'}
              onPress={() => handleToggleStatus(item)}
              textColor={theme.colors.primary}
              compact
              style={styles.actionButton}
            >
              {item.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <IconButton
              icon="trash-can-outline"
              size={18}
              onPress={() => handleDeleteTruck(item)}
              iconColor={theme.colors.error}
              style={styles.deleteButton}
            />
          </View>
        </Surface>
      </TouchableOpacity>
    );
  };

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <MaterialCommunityIcons name="truck-outline" size={72} color={theme.colors.outlineVariant} />
      <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>
        {searchQuery || filterStatus !== 'all' ? 'No trucks match' : 'No trucks yet'}
      </Text>
      <Text style={[styles.emptyMessage, { color: theme.colors.onSurfaceVariant }]}>
        {searchQuery || filterStatus !== 'all'
          ? 'Try adjusting your search or filter'
          : 'Add your first truck to start tracking trips and services'}
      </Text>
    </View>
  );

  const isLoading = subscriptionLoading;

  if (isLoading && displayTrucks.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={[styles.loadingText, { color: theme.colors.onSurfaceVariant }]}>
          Loading fleet...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        data={displayTrucks}
        renderItem={renderTruckItem}
        keyExtractor={item => item.id || ''}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={renderEmptyState}
        contentContainerStyle={[
          styles.listContent,
          displayTrucks.length === 0 && styles.emptyContainer,
        ]}
      />

      <ConfirmDeleteDialog
        visible={deleteDialogVisible}
        onDismiss={() => { setDeleteDialogVisible(false); setTruckToDelete(null); }}
        onConfirm={confirmDeleteTruck}
        title="Delete Truck"
        message="Are you sure you want to delete this truck? This action cannot be undone."
        itemSummary={
          truckToDelete
            ? `${truckToDelete.plateNumber} • ${truckToDelete.model && truckToDelete.color
              ? `${truckToDelete.model} (${truckToDelete.color})`
              : truckToDelete.model || truckToDelete.color || 'No details'
            }`
            : undefined
        }
        loading={deletingTruck}
      />

      <Portal>
        <Dialog
          visible={errorDialog.visible}
          onDismiss={() => setErrorDialog({ visible: false, title: '', message: '' })}
        >
          <Dialog.Icon icon="alert-circle" />
          <Dialog.Title>{errorDialog.title}</Dialog.Title>
          <Dialog.Content><Paragraph>{errorDialog.message}</Paragraph></Dialog.Content>
          <Dialog.Actions>
            <Button mode="contained" onPress={() => setErrorDialog({ visible: false, title: '', message: '' })}>
              OK
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  listContent: {
    paddingHorizontal: 16,
    paddingBottom: 88,
    gap: 10,
  },
  card: {
    borderRadius: 12,
  },
  cardTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 14,
    paddingBottom: 10,
  },
  vehicleIconBox: {
    width: 52,
    height: 52,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  cardMainInfo: {
    flex: 1,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  plateText: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 16,
    flex: 1,
    marginRight: 8,
  },
  modelText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    lineHeight: 18,
  },
  statusChip: {
    borderRadius: 9999,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  statusText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    lineHeight: 16,
  },
  chevron: {
    marginLeft: 4,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    borderTopWidth: StyleSheet.hairlineWidth,
    marginHorizontal: 14,
    paddingVertical: 8,
  },
  metaItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metaLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
  },
  metaValue: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
  },
  metaDivider: {
    width: StyleSheet.hairlineWidth,
    marginHorizontal: 12,
    alignSelf: 'stretch',
  },
  actionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 8,
    paddingRight: 4,
    paddingBottom: 4,
  },
  actionButton: {
    marginLeft: 0,
  },
  deleteButton: {
    margin: 0,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 32,
  },
  emptyTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  emptyContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    fontFamily: 'Inter-Regular',
    marginTop: 16,
    fontSize: 14,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 4,
  },
  overdueChip: { backgroundColor: '#ffdad6', height: 22, borderRadius: 9999 },
  overdueChipText: { fontFamily: 'Inter-Medium', color: '#ba1a1a', fontSize: 10 },
  dueSoonChip: { backgroundColor: '#ffb59a', height: 22, borderRadius: 9999 },
  dueSoonChipText: { fontFamily: 'Inter-Medium', color: '#380d00', fontSize: 10 },
});

export default TruckList;
